import { signal } from "@preact/signals";
import { useEffect, useRef, useState } from "preact/hooks";
import { anchorISO, listDirection } from "../state/store";
import { listFiltersFor, type SegmentConfig } from "../data/queries";
import { csvExportUrl } from "../data/api";

/** Openable from the count bar — the same module-level signal pattern as AboutDialog. */
const downloadOpen = signal(false);
export function openDownload() {
  downloadOpen.value = true;
}

type Scope = "upcoming" | "past" | "both";
type Layout = "concerts" | "works";
type Kind = "live" | "extra" | "archive";

/** Rows fetched per request — the server's CSV page cap. */
const PAGE = 5000;
/** Most concerts one browser download will assemble. Beyond this the API pages (see
 *  /api-docs/) are the right tool: a 50k-concert file is already ~40 MB. */
const CAP = 50000;

interface SourceState {
  kind: Kind;
  total: number | null;
  error: string | null;
}

const FILE_NAME: Record<Kind, string> = { live: "listings", extra: "other-events", archive: "archive" };

function filtersOf(cfg: SegmentConfig, kind: Kind) {
  return kind === "live" ? cfg.live : kind === "extra" ? cfg.extra : cfg.hist;
}

function today(): string {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
}

/** "Download as CSV": exports exactly what the list below the map is showing — the active
 *  filters, the time window, the Upcoming/Past side and the map view — so the spreadsheet
 *  matches the screen. Live listings and archive events are different kinds of record (a
 *  datetime and a source URL vs a fuzzy historical date and a citation), so they download as
 *  two files rather than one table that would have to leave half its columns empty. */
export function DownloadDialog() {
  const open = downloadOpen.value;
  const ref = useRef<HTMLDialogElement>(null);
  const anchored = anchorISO.value !== null;
  const [scope, setScope] = useState<Scope>("upcoming");
  const [layout, setLayout] = useState<Layout>("concerts");
  const [sources, setSources] = useState<SourceState[]>([]);
  const [busy, setBusy] = useState<{ kind: Kind; done: number; of: number } | null>(null);
  const [failure, setFailure] = useState<string | null>(null);
  const abort = useRef<AbortController | null>(null);

  useEffect(() => {
    const dlg = ref.current;
    if (!dlg) return;
    if (open && !dlg.open) {
      setScope(anchorISO.value === null ? "both" : listDirection.value === "backward" ? "past" : "upcoming");
      setFailure(null);
      dlg.showModal();
    } else if (!open && dlg.open) dlg.close();
  }, [open]);

  // Count what each file would hold whenever the choice changes (a limit=1 request per source).
  useEffect(() => {
    if (!open) return;
    const cfg = listFiltersFor(anchored ? scope : "both");
    const kinds: Kind[] = [];
    if (cfg.live) kinds.push("live");
    if (cfg.extra) kinds.push("extra");
    if (cfg.hist) kinds.push("archive");
    const ctl = new AbortController();
    setSources(kinds.map((kind) => ({ kind, total: null, error: null })));
    kinds.forEach((kind) => {
      const filters = filtersOf(cfg, kind)!;
      const url = csvExportUrl(kind, filters, { rows: kind === "archive" ? "events" : "concerts", limit: 1 });
      fetch(url, { signal: ctl.signal })
        .then((res) => {
          if (!res.ok) throw new Error(`${res.status}`);
          const total = Number(res.headers.get("x-total-count") ?? "0");
          setSources((prev) => prev.map((s) => (s.kind === kind ? { ...s, total } : s)));
        })
        .catch((err) => {
          if ((err as Error).name === "AbortError") return;
          setSources((prev) => prev.map((s) => (s.kind === kind ? { ...s, error: "couldn’t count" } : s)));
        });
    });
    return () => ctl.abort();
  }, [open, scope, anchored]);

  async function download(kind: Kind, total: number) {
    const cfg = listFiltersFor(anchored ? scope : "both");
    const filters = filtersOf(cfg, kind);
    if (!filters) return;
    const rows = layout === "works" ? "works" : kind === "archive" ? "events" : "concerts";
    const want = Math.min(total, CAP);
    const ctl = new AbortController();
    abort.current = ctl;
    setFailure(null);
    setBusy({ kind, done: 0, of: want });
    const parts: string[] = [];
    try {
      for (let offset = 0; offset < want; offset += PAGE) {
        const limit = Math.min(PAGE, want - offset);
        const res = await fetch(csvExportUrl(kind, filters, { rows, limit, offset, bom: offset === 0 }), {
          signal: ctl.signal,
        });
        if (!res.ok) throw new Error(`the server answered ${res.status}`);
        let text = await res.text();
        // Every page repeats the header row; keep only the first one.
        if (offset > 0) text = text.slice(text.indexOf("\r\n") + 2);
        parts.push(text);
        setBusy({ kind, done: Math.min(want, offset + limit), of: want });
      }
      const blob = new Blob(parts, { type: "text/csv;charset=utf-8" });
      const href = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = href;
      const side = anchored ? scope : "window";
      a.download = `classical-concert-map_${FILE_NAME[kind]}_${side}_${layout}_${today()}.csv`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      setTimeout(() => URL.revokeObjectURL(href), 10_000);
    } catch (err) {
      if ((err as Error).name !== "AbortError") setFailure(`The download stopped: ${(err as Error).message}. Try again, or narrow the filters.`);
    } finally {
      setBusy(null);
      abort.current = null;
    }
  }

  const label = (kind: Kind) =>
    kind === "live" ? "Concert listings" : kind === "extra" ? "Non-classical and kids’ events" : "Historical archive";
  const unit = (kind: Kind) => (kind === "live" ? "concerts" : "events");

  return (
    <dialog
      ref={ref}
      class="about-dialog download-dialog"
      aria-label="Download as CSV"
      onClose={() => {
        abort.current?.abort();
        downloadOpen.value = false;
      }}
      onClick={(e) => {
        if (e.target === ref.current) ref.current?.close();
      }}
    >
      <h2>Download as CSV</h2>
      <p>
        Downloads the concerts in the list: your current filters, time period and map view.
        Zoom the map out to include everywhere.
      </p>

      {anchored && (
        <fieldset class="download-choice">
          <legend>Which concerts</legend>
          {(["upcoming", "past", "both"] as Scope[]).map((s) => (
            <label key={s}>
              <input type="radio" name="dl-scope" checked={scope === s} onChange={() => setScope(s)} disabled={busy !== null} />
              {s === "upcoming" ? "Upcoming" : s === "past" ? "Past" : "Both"}
            </label>
          ))}
        </fieldset>
      )}

      <fieldset class="download-choice">
        <legend>One row per</legend>
        <label>
          <input type="radio" name="dl-layout" checked={layout === "concerts"} onChange={() => setLayout("concerts")} disabled={busy !== null} />
          concert
        </label>
        <label>
          <input type="radio" name="dl-layout" checked={layout === "works"} onChange={() => setLayout("works")} disabled={busy !== null} />
          piece performed
        </label>
      </fieldset>

      <ul class="download-files">
        {(sources.length === 0 || sources.every((s) => s.total === 0)) && (
          <li class="download-empty">Nothing to download for these filters.</li>
        )}
        {sources.filter((s) => s.total !== 0).map((s) => {
          const over = s.total !== null && s.total > CAP;
          const running = busy?.kind === s.kind;
          return (
            <li key={s.kind}>
              <span class="download-file-name">
                {label(s.kind)}
                <span class="download-file-count">
                  {s.error ? ` — ${s.error}` : s.total === null ? " — counting…" : ` — ${s.total.toLocaleString()} ${unit(s.kind)}`}
                </span>
              </span>
              {running ? (
                <span class="download-progress" role="status">
                  <progress max={busy!.of} value={busy!.done} />
                  <button type="button" class="about-close" onClick={() => abort.current?.abort()}>
                    Cancel
                  </button>
                </span>
              ) : (
                <button
                  type="button"
                  class="about-close download-go"
                  disabled={busy !== null || !s.total}
                  onClick={() => s.total && download(s.kind, s.total)}
                >
                  {over ? `Download first ${CAP.toLocaleString()}` : "Download"}
                </button>
              )}
              {over && (
                <p class="download-note">
                  That is more than one file should hold. Narrow the filters, or page through
                  everything with the <a href="/api-docs/#csv">API</a>.
                </p>
              )}
            </li>
          );
        })}
      </ul>

      {failure && (
        <p class="download-note" role="alert">
          {failure}
        </p>
      )}

      <p class="about-credits">
        Dates are local time at the venue. Every listing links to the page it came from.
        Building something? See the <a href="/api-docs/">API documentation</a>.
      </p>
      <button type="button" class="about-close" onClick={() => ref.current?.close()}>
        Close
      </button>
    </dialog>
  );
}
